import React, { createContext, useContext } from 'react';
import { ListWithPagination } from '@anytag/shared-molecules';
import { SocialAccountInfluencerListSortFieldV2 } from '@anytag/graphql';
import { useInfluencerParamsSearch } from '../hooks';
import { InfluencersContextReturnType } from '../schemaTypes';
import { useTiktokInfluencersList } from './hooks';
import {
  TiktokInfluencersProps,
  TiktokInfluencerProfile,
  TiktokInfluencersFilterFormValues,
  TiktokInfluencersSearchSchemaType
} from './schemaTypes';

type ContextType = InfluencersContextReturnType<
  TiktokInfluencerProfile,
  TiktokInfluencersFilterFormValues,
  SocialAccountInfluencerListSortFieldV2
>;

const Context = createContext<ContextType>({} as ContextType);

interface Props {
  value: TiktokInfluencersProps;
  children: React.ReactElement | Array<React.ReactElement>;
}

export const TiktokInfluencersProvider = ({
  children,
  value: {
    isTtcmCampaign,
    isPackagePage,
    isEngagementPage,
    getCurrentProfiles,
    getSortSelectButtonProps,
    getListSelectButtonProps
  }
}: Props) => {
  const paramsSearch = useInfluencerParamsSearch<TiktokInfluencersSearchSchemaType>();
  const { page, sort, limit, filter } = paramsSearch;
  const listProps = useTiktokInfluencersList({ page, sort, limit, filter, getCurrentProfiles });

  return (
    <Context.Provider
      value={{
        ...paramsSearch,
        ...listProps,
        isPackagePage,
        isTtcmCampaign,
        isEngagementPage,
        getListSelectButtonProps,
        sortSelectButtonProps: getSortSelectButtonProps?.(listProps.listRecords, listProps.totalRecords)
      }}
    >
      <ListWithPagination pagination={{ totalRecords: listProps.totalRecords }}>{children}</ListWithPagination>
    </Context.Provider>
  );
};

export const useTiktokInfluencersContext = () => useContext<ContextType>(Context);
